<template>
  <div class="registration-details ma-1 pa-3">
    <v-overlay :value="isValidating || isSaving || isGeneratingDogId || isLoadingPuppyInfo" absolute>
      <v-progress-circular indeterminate color="primary" />
    </v-overlay>
    <v-dialog v-model="showValidationDialog">
      <v-card class="search-modal">
        <h2>Validation Results</h2>
        <v-form class="search-form">
          <v-alert
            v-if="
              !!validationResult &&
              !!validationResult.errors &&
              validationResult.errors.length > 0
            "
            type="error"
          >
            <pre>{{ validationResult.errors.join("\r\n") }}</pre>
          </v-alert>
          <v-alert
            v-if="
              !!validationResult &&
              !!validationResult.warnings &&
              validationResult.warnings.length > 0
            "
            type="warning"
            outlined
          >
            <pre>{{ validationResult.warnings.join("\r\n") }}</pre>
            <pre>{{ puppyRefreshChanges.join("\r\n") }}</pre>
          </v-alert>
          <v-btn
            large
            color="primary"
            width="125px"
            :disabled="isSaving || isValidating || !validationResult.canBeSaved"
            :loading="isSaving || isValidating"
            v-on:click="saveChanges"
          >
            <v-icon v-if="!validationResult.canBeSaved">mdi-alert</v-icon>
            Save</v-btn
          >
        </v-form>
      </v-card>
    </v-dialog>
    <v-dialog v-model="showPuppyRefreshChanges">
      <v-card class="search-modal">
        <h2>Puppy data refresh changes</h2>
        <v-form class="search-form">          
          <v-alert
            v-if="
              !!puppyRefreshChanges &&
              puppyRefreshChanges.length > 0
            "
            type="warning"
            outlined
          >
            <pre>{{ puppyRefreshChanges.join("\r\n") }}</pre>
          </v-alert>
        </v-form>
      </v-card>
    </v-dialog>
    <v-row class="mt-2">
      <v-alert
        width="100%"
        class="pr-2 pl-2"
        v-if="errorMessage"
        dismissible
        type="error"
      >
        {{ errorMessage }}</v-alert
      >
    </v-row>
    <v-row v-if="dogId && !isEditMode" class="mt-2" justify="end">
      <v-btn class="mt-2 ml-1 mr-1" color="secondary" width="100px" :disabled="registrationLoading" @click="toggleEditMode">
        Edit
      </v-btn>
    </v-row>
    <v-row v-if="dogId && isEditMode" class="mt-2" align="start">
      <v-textarea
        outlined
        auto-grow
        clearable
        rows="1"
        v-model="dogInfo.adminNotes"
        label="Description of Changes"
        :error-messages="requiredFieldMessages(dogInfo.adminNotes)"
      />
      <v-btn class="mt-2 ml-1 mr-1" color="secondary" width="100px" :disabled="isSaving" @click="cancelEdit">
        Cancel
      </v-btn>
      <v-btn
        class="mt-2 ml-1 mr-1"
        color="primary"
        width="100px"
        :disabled="isChangingSire || isSaving || isValidating || isGeneratingDogId || isLoadingPuppyInfo || !canSaveDog"
        :loading="isSaving || isValidating"
        @click="validateAndConfirmSavingChanges"
      >
        Save
      </v-btn>
    </v-row>
    <v-row class="mt-2">
      <v-row>
        <v-col>            
          <v-text-field
            v-model="dogInfo.puppyId"
            label="Puppy id"
            disabled
            outlined
          />
        </v-col>
        <v-col>      
          <v-text-field
            v-model="dogInfo.litterId"
            label="Litter id"
            disabled
            outlined
          />
        </v-col>
        <v-col>
          <v-btn class="mt-5" icon v-on:click="showPuppiesSearchDialog = true" :disabled="!isEditMode" color="primary">
            <v-icon>mdi-magnify</v-icon>
          </v-btn>    
          <v-btn class="mt-5" icon v-on:click="clearPuppySelection" :disabled="!isEditMode || !(!!dogInfo.litterId || !!dogInfo.puppyId)" color="secondary">
            <font-awesome-icon icon="times"/>
          </v-btn>  
          <v-btn class="mt-5" icon v-on:click="refreshPuppyData" :disabled="!isEditMode || !(!!dogInfo.litterId || !!dogInfo.puppyId)" color="primary">
            <v-icon>mdi-refresh</v-icon>
          </v-btn>      
          <AdminLitterPuppySearchDialog 
            :litterId="dogInfo.litterId" 
            :isEditMode="isEditMode" 
            v-bind:showPuppiesSearchDialog.sync="showPuppiesSearchDialog" 
            :onPuppySelected="onPuppySelected" />
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-checkbox v-model="dogInfo.ascaRegistered" value disabled>
            <template v-slot:label>
              <span>ASCA Registered</span>
            </template>
          </v-checkbox>
        </v-col>
        <v-col>
          <v-autocomplete
            v-model="dogInfo.registrationType"
            label="Registration type"
            :items="colors.registrationTypes"
            item-text="description"
            item-value="abbreviation"
            :disabled="!isEditMode || isAscaPuppyRegistration"
            :error-messages="requiredFieldMessages(dogInfo.registrationType)"
            clearable
          ></v-autocomplete>
        </v-col>
      </v-row>
    </v-row>
    <v-row class="mt-2">
      <v-row class="mr-2">
        <v-col>
          <DatepickerFormatted
            :disabled="!isEditMode || !!dogInfo.puppyId"
            :date="dogInfo.birthdate"
            label="Birth Date"
            :max="ascaToday"
            :rules="[dateNotInFuture]"
            v-on:updateDates="updateDates($event, 'birthdate')"
          />
          <DatepickerFormatted
            v-if="dogInfo.isDead"
            :disabled="!isEditMode"
            :date="dogInfo.deathDate"
            label="Date of death (optional)"
            :max="ascaToday"
            :rules="[dateNotInFuture, deathNotBeforeBirth]"
            v-on:updateDates="updateDates($event, 'deathDate')"
          />
        </v-col>
        <v-col>
          <v-checkbox v-model="dogInfo.isDead" value :disabled="!isEditMode">
            <template v-slot:label>
              <span>Dead</span>
            </template>
          </v-checkbox>
        </v-col>
        <v-col>
          <div class="mb-1">Member photo</div>
          <v-img
            v-if="dogInfo.photoUrl"
            :src="dogInfo.photoUrl"
            :aspect-ratio="1"
            max-width="160"
            class="rounded grey lighten-4 mb-1"
          />
          <span v-else class="caption grey--text d-block mb-1">None uploaded</span>
          <input
            ref="adminPhotoInput"
            type="file"
            accept="image/jpeg,image/png,image/webp"
            class="d-none"
            @change="handleAdminPhotoSelected"
          />
          <div>
            <v-btn small text color="primary" :disabled="!dogId" :loading="savingPhoto" @click="chooseDogPhoto">
              {{ dogInfo.photoUrl ? "Change photo" : "Add photo" }}
            </v-btn>
            <v-btn
              v-if="dogInfo.photoUrl"
              small
              text
              color="red"
              :disabled="!dogId"
              :loading="removingPhoto"
              @click="removeDogPhoto"
            >
              Remove
            </v-btn>
          </div>
          <div v-if="photoError" class="red--text caption">{{ photoError }}</div>
        </v-col>
        <v-col>
          <v-select
            v-model="dogInfo.sex"
            label="Gender"
            :items="colors.genders"
            item-text="description"
            item-value="abbreviation"
            :disabled="!isEditMode || isAscaPuppyRegistration"
          ></v-select>
        </v-col>
      </v-row>
      <v-col>
        <DatepickerFormatted
          :disabled="!isEditMode"
          :date="dogInfo.registrationDate"
          label="Registration Date"
          :max="ascaToday"
          :rules="[(v) => !!v || 'Required', dateNotInFuture]"
          v-on:updateDates="updateDates($event, 'registrationDate')"
        />
      </v-col>
    </v-row>
    <v-row class="mt-2">
      <v-col>
        <AdminDogSearchDialog
          label="Dam ID"
          v-bind:dogId.sync="dogInfo.damId"
          :initialDogId="persistedDogInfo.damId"
          :isEditMode="isEditMode || isAscaPuppyRegistration"
          :hideSearch="isAscaPuppyRegistration"
          :showOwners="true"
        />
          <v-btn
            color="primary"
            v-on:click="isChangingSire = !isChangingSire"
            :disabled="!isEditMode || !dogId"
          >
          Change sire
        </v-btn> 
        <v-btn
          v-if="isChangingSire"
          color="primary"
          v-on:click="changeSire"
          :disabled="!isEditMode && !isChangingSire"
        >
          Confirm sire change & save
        </v-btn> 
        <AdminDogSearchDialog
          v-if="isChangingSire"
          label="New Sire ID"
          v-bind:dogId.sync="newSireId"
          :initialDogId="newSireId"
          :isEditMode="isEditMode && isChangingSire"
          :showOwners="true"
        />
      </v-col>
      <v-col>
        <AdminDogSearchDialog
          label="Sire ID"
          v-bind:dogId.sync="dogInfo.sireId"
          :initialDogId="persistedDogInfo.sireId"
          :isEditMode="(isEditMode || isAscaPuppyRegistration) && !isChangingSire"
          :hideSearch="isAscaPuppyRegistration"
          :showOwners="true"
        />
      </v-col>
      <v-col>
        <AdminDogSearchDialog
          label="Previous Sire ID"
          v-bind:dogId.sync="dogInfo.prevSireId"
          :initialDogId="persistedDogInfo.prevSireId"
          :isEditMode="false"
          :showOwners="true"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col class="mr-2">
        <v-row>
          <v-col>
            <v-btn
              color="primary"
              v-on:click="generateDogId"
              :disabled="!isEditMode || isValidating || isSaving || isGeneratingDogId || !canGenerateNewDogId"
              :loading="isGeneratingDogId"
            >
              Generate new ID
            </v-btn>
          </v-col>
          <v-col>
            <v-text-field
              v-model="dogInfo.dogId"
              label="Dog ID"
              clearable
              maxlength="12"
              counter="12"
              :disabled="!isEditMode || !hasRegistrationType"
              :messages="!hasRegistrationType ? ['Select registration type'] : []"
              :error-messages="hasRegistrationType ? requiredFieldMessages(dogInfo.dogId) : []"
              :rules="[(v) => !v || v.trim().length <= 12 || 'Max 12 characters']"
            />
          </v-col>
          <v-col>
            <v-checkbox
              v-model="dogInfo.notForBreeding"
              value
              :disabled="!isEditMode"
            >
              <template v-slot:label>
                <span>Not for breeding</span>
              </template>
            </v-checkbox>
            <v-checkbox
              class="ma-0 pa-0"
              v-model="dogInfo.isRevoked"
              :disabled="
                !isEditMode ||
                !(
                  dogInfo &&
                  dogInfo.registrationType &&
                  revokableRegistrationTypes.includes(
                    dogInfo.registrationType.trim()
                  )
                ) ||
                (dogInfo && !dogInfo.dogId)
              "
              value
            >
              <template v-slot:label>
                <span>Revoked</span>
              </template>
            </v-checkbox>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-text-field
              v-model="dogInfo.name"
              label="Dog Name"
              :disabled="!isEditMode"
              outlined
              :error-messages="requiredFieldMessages(dogInfo.name)"
              :rules="[(v) => !v || v.length <= 50 || 'Max 50 characters']"
              counter="50"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-text-field
              v-model="dogInfo.callName"
              label="Call Name"
              :disabled="!isEditMode"
              outlined
              :rules="[
                (v) => !v || (!!v && v.length <= 50) || 'Max 50 characters',
              ]"
              counter="50"
            />
          </v-col>
          <v-col>
            <v-text-field
              v-model="dogInfo.tattoo"
              label="Tattoo/Chip No"
              :disabled="!isEditMode"
              outlined
            />
          </v-col>
        </v-row>  
      </v-col>
      <v-col>
        <v-row>
          <v-col>
            <v-autocomplete
              v-model="dogInfo.breed"
              label="Breed"
              :items="colors.breeds"
              item-text="description"
              item-value="abbreviation"
              :disabled="
                !isEditMode ||
                isAscaPuppyRegistration ||
                (dogInfo &&
                  dogInfo.registrationType &&
                  ascaDogsRegistrations.includes(
                    dogInfo.registrationType.trim()
                  ))
              "
            ></v-autocomplete>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-select
              v-model="dogInfo.body"
              label="Body color"
              :items="colors.mainColors"
              item-text="description"
              item-value="abbreviation"
              :disabled="!isEditMode || isAscaPuppyRegistration"
            ></v-select>
          </v-col>
          <v-col>
            <v-select
              v-model="dogInfo.trim"
              label="Trim color"
              :items="colors.trimColors"
              item-text="description"
              item-value="abbreviation"
              :disabled="!isEditMode"
            ></v-select>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-select
              v-model="dogInfo.leftEye"
              label="Left eye color"
              :items="colors.eyeColors"
              item-text="description"
              item-value="abbreviation"
              :disabled="!isEditMode"
            ></v-select>
          </v-col>
          <v-col>
            <v-select
              v-model="dogInfo.rightEye"
              label="Right eye color"
              :items="colors.eyeColors"
              item-text="description"
              item-value="abbreviation"
              :disabled="!isEditMode"
            ></v-select>
          </v-col>
        </v-row>
        <v-row class="ml-2" style="min-height: 130px">
          <AdminEditableCoownersList
            :isEditMode="isEditMode"
            :owners="dogInfo.owners"
            :ownershipType="dogOwnershipType"
            v-on:updateSelectedPeople="updateSelectedPeople($event)"
          />
        </v-row>
      </v-col>
    </v-row>
    <v-row v-if="isEditMode" class="mt-2 mb-3" justify="end">
      <v-btn
        v-if="dogId && isEditMode"
        class="ml-1 mr-1"
        color="secondary"
        width="100px"
        :disabled="isSaving"
        @click="cancelEdit"
      >
        Cancel
      </v-btn>
      <v-btn
        class="ml-1 mr-1"
        color="primary"
        width="100px"
        :disabled="!isEditMode || isChangingSire || isSaving || isValidating || isGeneratingDogId || isLoadingPuppyInfo || !canSaveDog"
        :loading="isSaving || isValidating"
        @click="validateAndConfirmSavingChanges"
      >
        Save
      </v-btn>
    </v-row>
    <v-divider v-if="dogId" class="my-3" />
    <section v-if="dogId" class="mt-3">
      <h3>Notes</h3>
      <p v-if="dogInfo.notes" class="notes-text mt-2">{{ dogInfo.notes }}</p>
      <p v-else class="mt-2 grey--text">No notes.</p>
    </section>
    <v-snackbar v-model="showInfoMessage" title color="green">
      {{ infoMessage }}
        <template v-slot:action="{ attrs }">
          <v-btn color="primary" text v-bind="attrs" @click="showInfoMessage = false">
            Close
          </v-btn>
        </template>
      </v-snackbar>
    <v-snackbar v-model="showErrorMessage" title color="red">
        {{ errorMessage }}
        <template v-slot:action="{ attrs }">
          <v-btn color="primary" text v-bind="attrs" @click="showErrorMessage = false">
            Close
          </v-btn>
        </template>
    </v-snackbar>
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import DatepickerFormatted from "../../../common/inputs/DatepickerFormatted.vue";
import AdminEditableCoownersList, {
  OwnershipType,
} from "../../AdminEditableCoownersList.vue";
import AdminDogSearchDialog from "../../AdminDogSearchDialog.vue";
import AdminLitterPuppySearchDialog from "../../AdminLitterPuppySearchDialog.vue";

import dogService from "@/services/admin/dogService";
import { resizeImage } from "@/helpers/resize-image";
import { getErrorMessage } from "@/services/errorHandler";
import lookupsService from "@/services/lookupsService";
import { getAscaToday } from "@/helpers/date-utils";

import { Messages } from "@/constants";

export default Vue.extend({
  name: "DogRegistrationDetails",
  components: {
    AdminEditableCoownersList,
    DatepickerFormatted,
    AdminDogSearchDialog,
    AdminLitterPuppySearchDialog,
  },
  props: {
    dogId: String,
  },
  data: function () {
    const ascaToday = getAscaToday();
    return {
      ascaToday,
      removingPhoto: false,
      savingPhoto: false,
      photoError: "",
      newSireId: "",
      dogInfo: {
        dogId: "",
        adminNotes: "",
        owners: [] as any,
        puppyId: null,
        registrationDate: ascaToday,
        isRevoked: false,
        notForBreeding: false,
      } as any,
      persistedDogInfo: {
        damId: null,
        sireId: null,
        prevSireId: null,
      } as any,
      puppyInfo: {
        dogId: "",
      } as any,
      isEditMode: false,
      colors: {},
      selectedPuppyId: null,
      showValidationDialog: false,
      validationResult: { errors: [] as string[], warnings: [] as string[]},
      registrationsWithAutomaticIdGeneration: ["E", "F", "HIII", "LEP", "T"],
      ascaDogsRegistrations: ["E", "F", "HIII", "LEP"],
      revokableRegistrationTypes: ["E", "F"],
      showPuppiesSearchDialog: false,
      errorMessage: '',
      infoMessage: '',
      showErrorMessage: false,
      showInfoMessage: false,
      isChangingSire: false,
      puppyRefreshChanges: [] as string [],
      showPuppyRefreshChanges: false,
      validationSequence: 0,
      saveSequence: 0,
      isSaving: false,
      isValidating: false,
      registrationLoadSequence: 0,
      registrationLoading: false,
      dogInfoBeforeEdit: null as any,
      validatedDogInfo: null as any,
      editRequestSequence: 0,
      isGeneratingDogId: false,
      isLoadingPuppyInfo: false,
    };
  },
  computed: {
    hasRegistrationType: function (): boolean {
      return !!this.$data.dogInfo?.registrationType?.trim();
    },
    dogOwnershipType: function () {
      return OwnershipType.Dog;
    },
    isAscaPuppyRegistration: function () {
      return !(
        this.$data.dogInfo.puppyId == null || !this.$data.dogInfo.puppyId
      );
    },
    canGenerateNewDogId: function (): boolean {
      return !!(
        this.hasRegistrationType &&
        this.$data.registrationsWithAutomaticIdGeneration.includes(
          this.$data.dogInfo.registrationType.trim()
        )
      );
    },
    canSaveDog: function () {
      const normalizeSingleLine = (value: string | null | undefined) => value?.trim().replace(/\s+/g, " ");
      const dogId = normalizeSingleLine(this.$data.dogInfo?.dogId);
      const name = normalizeSingleLine(this.$data.dogInfo?.name);
      const callName = normalizeSingleLine(this.$data.dogInfo?.callName);
      const registrationType = normalizeSingleLine(this.$data.dogInfo?.registrationType);
      const registrationDate = this.$data.dogInfo?.registrationDate;
      const birthdate = this.$data.dogInfo?.birthdate;
      const deathDate = this.$data.dogInfo?.isDead ? this.$data.dogInfo?.deathDate : null;
      const adminNotes = this.$data.dogInfo?.adminNotes?.trim();
      const datesNotInFuture = [birthdate, deathDate, registrationDate]
        .every((date) => !date || Temporal.PlainDate.compare(date, this.ascaToday) <= 0);
      const deathDateIsValid = !birthdate || !deathDate || Temporal.PlainDate.compare(deathDate, birthdate) >= 0;

      return !!dogId && dogId.length <= 12 && !!name && name.length <= 50 && (!callName || callName.length <= 50) && !!registrationType && !!registrationDate && !!adminNotes && datesNotInFuture && deathDateIsValid;
    },
  },
  created: function () {
    this.reloadData();
  },
  methods: {
    trimDogTextFields: function () {
      if (typeof this.dogInfo.adminNotes === "string") {
        this.dogInfo.adminNotes = this.dogInfo.adminNotes.trim();
      }
      [
        "dogId",
        "name",
        "callName",
        "tattoo",
        "registrationType",
        "sex",
        "sireId",
        "damId",
        "prevSireId",
        "litterId",
        "breed",
        "body",
        "trim",
        "leftEye",
        "rightEye",
      ].forEach((field) => {
        if (typeof this.dogInfo[field] === "string") {
          this.dogInfo[field] = this.dogInfo[field].trim().replace(/\s+/g, " ");
        }
      });
    },
    dateNotInFuture: function (value: Temporal.PlainDate | null): boolean | string {
      return !value || Temporal.PlainDate.compare(value, this.ascaToday) <= 0 || "Cannot be in the future";
    },
    deathNotBeforeBirth: function (value: Temporal.PlainDate | null): boolean | string {
      const birthdate = this.dogInfo.birthdate as Temporal.PlainDate | null;
      return !value || !birthdate || Temporal.PlainDate.compare(value, birthdate) >= 0 || "Cannot be before birth date";
    },
    requiredFieldMessages: function (value: string | null | undefined): string[] {
      return value?.trim() ? [] : ["Required"];
    },
    chooseDogPhoto: function () {
      this.photoError = "";
      (this.$refs.adminPhotoInput as HTMLInputElement).click();
    },
    handleAdminPhotoSelected: async function (event: Event) {
      const input = event.target as HTMLInputElement;
      const file = input.files && input.files[0];
      input.value = "";

      if (!file) return;

      this.savingPhoto = true;
      try {
        const resized = await resizeImage(file);
        const res = await dogService.uploadDogPhoto(this.dogInfo.dogId, resized);

        if (res.success) {
          this.reloadData();
        } else {
          this.photoError = res.errorMessage;
        }
      } catch (e: any) {
        this.photoError = getErrorMessage(e);
      } finally {
        this.savingPhoto = false;
      }
    },
    removeDogPhoto: async function () {
      this.removingPhoto = true;
      try {
        const res = await dogService.deleteDogPhoto(this.dogInfo.dogId);
        if (res.success) {
          this.dogInfo.photoUrl = null;
        }
      } finally {
        this.removingPhoto = false;
      }
    },
    reloadData: function () {
      this.editRequestSequence++;
      this.isGeneratingDogId = false;
      this.isLoadingPuppyInfo = false;
      const registrationLoadSequence = ++this.registrationLoadSequence;
      if (this.dogId) {
        this.registrationLoading = true;
        dogService
          .getDogRegistrationInfoById(this.dogId)
          .then((res: any) => {
            if (registrationLoadSequence !== this.registrationLoadSequence) return;
            this.dogInfo = Object.assign({ adminNotes: "" }, res);
            this.persistedDogInfo = {
              damId: res.damId,
              sireId: res.sireId,
              prevSireId: res.prevSireId,
            };
            this.selectedPuppyId = this.dogInfo.puppyId;
          })
          .catch((error) => {
            if (registrationLoadSequence === this.registrationLoadSequence) this.$handleError(error);
          })
          .finally(() => {
            if (registrationLoadSequence === this.registrationLoadSequence) this.registrationLoading = false;
          });
      } else {
        this.registrationLoading = false;
        this.dogInfo = {
          dogId: "",
          adminNotes: "New Dog Entry",
          owners: [] as any,
          puppyId: null,
          registrationDate: this.ascaToday,
        };
        this.selectedPuppyId = null;
        this.isEditMode = true;
      }
      lookupsService.getDogsAttributes().then((res) => {
        if (registrationLoadSequence !== this.registrationLoadSequence) return;
        this.colors = res;
        this.showValidationDialog = false;
        this.validationResult = { errors: [], warnings: []};
        this.errorMessage = "";
      });
      
      this.puppyRefreshChanges = [];
    },
    toggleEditMode: function () {
      this.dogInfoBeforeEdit = {
        ...this.dogInfo,
        owners: this.dogInfo.owners?.map((owner: any) => ({ ...owner })),
      };
      this.isEditMode = true;
    },
    cancelEdit: function () {
      this.validationSequence++;
      this.editRequestSequence++;
      this.isValidating = false;
      this.isGeneratingDogId = false;
      this.validatedDogInfo = null;
      if (this.dogInfoBeforeEdit) {
        this.dogInfo = this.dogInfoBeforeEdit;
        this.dogInfoBeforeEdit = null;
      }
      this.isChangingSire = false;
      this.newSireId = "";
      this.isEditMode = false;
      this.reloadData();
    },
    updateDates: function (val: any, key: string) {
      this.dogInfo[key] = val.date;
      this.dogInfo = Object.assign({}, this.dogInfo);
    },
    updateSelectedPeople: function (val: any) {
      //workaround for setting up the primary owner
      //and issues with reactivity in Vue https://v2.vuejs.org/v2/guide/reactivity.html?redirect=true#For-Arrays
      if (
        val.newPeople &&
        (val.newPeople.length === 1 ||
          (val.newPeople.length > 0 &&
            val.newPeople.findIndex((own: any) => own.isPrimary) == -1))
      ) {
        val.newPeople[0].isPrimary = true;
      }
      if (!!this.dogInfo.owners && val.newPeople && val.newPeople.length > 0) {
        this.dogInfo.owners.splice(val.newPeople.length);
      }
      this.$set(this.dogInfo, "owners", val.newPeople);
    },
    onPuppySelected: function(puppyInfo: any){
      this.isGeneratingDogId = false;
      const editRequestSequence = ++this.editRequestSequence;
      this.isLoadingPuppyInfo = true;
      this.showPuppiesSearchDialog = false;
      dogService
        .getPuppyRegistrationInfoById(puppyInfo.id)
        .then((res: any) => {
          if (editRequestSequence !== this.editRequestSequence) return;
          this.dogInfo.body = res.body;
          this.dogInfo.sex = res.sex;
          this.dogInfo.litterId = res.litterId;
          this.dogInfo.birthdate = res.birthdate;
          this.dogInfo.damId = res.damId;
          this.dogInfo.sireId = res.sireId;
          this.dogInfo.breed = res.breed;
          this.dogInfo.puppyId = res.puppyId;
          this.puppyInfo.dogId = res.dogId;
          this.dogInfo.registrationType = "E";
          this.persistedDogInfo = {
              damId: res.damId,
              sireId: res.sireId
            };
        })
        .finally(() => {
          if (editRequestSequence === this.editRequestSequence) this.isLoadingPuppyInfo = false;
        });
      
      this.puppyRefreshChanges = [];
    },
    clearPuppySelection: function() {
      this.isGeneratingDogId = false;
      this.isLoadingPuppyInfo = false;
      this.editRequestSequence++;
      this.showPuppiesSearchDialog = false;
      this.dogInfo.litterId = null;
      this.dogInfo.puppyId = null;
      this.dogInfo.body = null;
      this.dogInfo.sex = null;
      this.dogInfo.birthdate = null;
      this.dogInfo.damId = null;
      this.dogInfo.sireId = null;
      this.dogInfo.breed = null;
      this.puppyInfo.dogId = null;
      this.dogInfo.registrationType = null;
      this.persistedDogInfo = {
        damId: null,
        sireId: null,
        AdminDogSearchDialog: null,
      };

      this.puppyRefreshChanges = [];
    },
    refreshPuppyData: function() {
      this.isGeneratingDogId = false;
      const editRequestSequence = ++this.editRequestSequence;
      this.isLoadingPuppyInfo = true;
      dogService
        .getPuppyRegistrationInfoById(this.dogInfo.puppyId)
        .then((res: any) => {
          if (editRequestSequence !== this.editRequestSequence) return;

          let warnings = [] as string[];  
          if(this.dogInfo.body != res.body){
            warnings.push(`Main color changed from '${this.dogInfo.body}' to '${res.body}'`);
          }
          if(this.dogInfo.sex != res.sex){
            warnings.push(`Gender changed from '${this.dogInfo.sex}' to '${res.sex}'`);
          }
          if(this.dogInfo.litterId != res.litterId){
            warnings.push(`Litter ID# from '${this.dogInfo.litterId}' to '${res.litterId}'`);
          }
          if(this.dogInfo.birthdate != res.birthdate){
            warnings.push(`Birth date changed from '${this.dogInfo.birthdate}' to '${res.birthdate}'`);
          }
          if(this.dogInfo.damId != res.damId?.trim()){
            warnings.push(`Dam ID# changed from '${this.dogInfo.damId}' to '${res.damId?.trim()}'`);
          }
          if(this.dogInfo.sireId != res.sireId?.trim()){
            warnings.push(`Sire ID# changed from '${this.dogInfo.sireId}' to '${res.sireId?.trim()}'`);
          }
          if(this.dogInfo.breed != res.breed){
            warnings.push(`Breed from '${this.dogInfo.breed}' to '${res.breed}'`);
          }
          if(this.dogInfo.puppyId != res.puppyId){
            warnings.push(`Puppy ID# from '${this.dogInfo.puppyId}' to '${res.puppyId}'`);
          }
          if(this.dogInfo.dogId != res.dogId){
            warnings.push(`Dog ID# from '${this.dogInfo.dogId}' to '${res.dogId}'`);
          }
          if(this.dogInfo.registrationType != 'E'){
            warnings.push(`Registration type from '${this.dogInfo.registrationType}' to 'E'`);
          }

          this.dogInfo.body = res.body;
          this.dogInfo.sex = res.sex;
          this.dogInfo.litterId = res.litterId;
          this.dogInfo.birthdate = res.birthdate;
          this.dogInfo.damId = res.damId?.trim();
          this.dogInfo.sireId = res.sireId?.trim();
          this.dogInfo.breed = res.breed;
          this.dogInfo.puppyId = res.puppyId;
          this.puppyInfo.dogId = res.dogId;
          this.dogInfo.registrationType = "E";
          this.persistedDogInfo = {
              damId: res.damId,
              sireId: res.sireId
            };

          if(warnings.length > 0) {
            this.puppyRefreshChanges = warnings;
            this.showPuppyRefreshChanges = true;
          }
        })
        .finally(() => {
          if (editRequestSequence === this.editRequestSequence) this.isLoadingPuppyInfo = false;
        });
    },
    generateDogId: function () {
      if (this.isValidating || this.isSaving || this.isGeneratingDogId || this.isLoadingPuppyInfo) return;
      const editRequestSequence = ++this.editRequestSequence;
      const registrationType = this.dogInfo.registrationType;
      this.isGeneratingDogId = true;
      dogService
        .generateDogId({ registrationType })
        .then((res: any) => {
          if (editRequestSequence !== this.editRequestSequence || registrationType !== this.dogInfo.registrationType) return;
          this.dogInfo.dogId = res.id;
          if (res.success && res.id) {
            this.dogInfo.dogId = res.id;
            this.dogInfo.isRevoked = false;
            this.dogInfo.notForBreeding = this.dogInfo.registrationType === 'LEP' ? true : false;
            this.errorMessage = "";
          } else {
            this.errorMessage =
              "Couldn't generate new ID." + res.messages.join(", ");
            this.showErrorMessage = true;
          }
        })
        .catch((e) => {
            if (editRequestSequence !== this.editRequestSequence || registrationType !== this.dogInfo.registrationType) return;
            this.errorMessage ="Couldn't generate new ID.";
            this.showErrorMessage = true;
        })
        .finally(() => {
          if (editRequestSequence !== this.editRequestSequence) return;
          this.isGeneratingDogId = false;
        });
    },
    validateAndConfirmSavingChanges: function () {
      if (this.isValidating || this.isSaving || this.isGeneratingDogId || this.isLoadingPuppyInfo) return;
      if (!this.dogId) this.dogInfo.adminNotes = "New Dog Entry";
      this.trimDogTextFields();
      const validationSequence = ++this.validationSequence;
      const dogId = this.dogId;
      var currentDogId = this.dogId ? this.dogId : "";
      this.validatedDogInfo = {
        ...this.dogInfo,
        currentDogId,
        owners: this.dogInfo.owners?.map((owner: any) => ({ ...owner })),
      };
      this.isValidating = true;
      dogService
        .validateDogRegistrationInfoEdit(this.validatedDogInfo)
        .then((res: any) => {
          if (validationSequence !== this.validationSequence || dogId !== this.dogId) return;
          this.validationResult = res;
          if (res.hasValidationIssues) {
            this.showValidationDialog = true;
          } else {
            this.saveChanges();
          }
        })
        .catch(() => {
          if (validationSequence !== this.validationSequence || dogId !== this.dogId) return;
          this.errorMessage = Messages.SaveError;
          this.showErrorMessage = true;
        })
        .finally(() => {
          if (validationSequence === this.validationSequence) this.isValidating = false;
        });
    },
    saveChanges: function () {
      if (this.isSaving || !this.validatedDogInfo) return;
      const saveSequence = ++this.saveSequence;
      const dogId = this.dogId;
      const dogInfo = this.validatedDogInfo;
      this.showValidationDialog = false;
      this.isSaving = true;
      dogService
        .editDogById(dogInfo.currentDogId, dogInfo)
        .then((res) => {
          if (saveSequence !== this.saveSequence || dogId !== this.dogId) return;
          if (res.success) {
            this.isEditMode = false;
            this.dogInfoBeforeEdit = null;
            if (res.id && res.id?.trim() != this.dogId?.trim()) {
              this.$navigate({ path: `/admin/dogs/${encodeURIComponent(res.id.trim())}` })
            } else {
              this.reloadData();
              this.$emit("saved");
            }
            this.showInfoMessage = true;
            this.infoMessage = Messages.SaveSuccessful;
          } else {
            this.errorMessage = res.messages.join(", ");
          }
        })
        .catch((e) => {
          if (saveSequence !== this.saveSequence || dogId !== this.dogId) return;
          this.errorMessage = Messages.SaveError;
          this.showErrorMessage = true;          
        })
        .finally(() => {
          if (saveSequence !== this.saveSequence) return;
          this.isSaving = false;
          this.validatedDogInfo = null;
        });
    },    
    changeSire: function() {
      const oldSireId = this.persistedDogInfo.sireId;
      const newSireId = this.newSireId;

      const notes = `Sire changed from ${oldSireId} to ${newSireId}.`;

      this.dogInfo.sireId = newSireId;
      this.dogInfo.prevSireId = oldSireId;
      this.dogInfo.adminNotes = !!this.dogInfo.adminNotes ? this.dogInfo.adminNotes + " | " + notes : notes;

      this.validateAndConfirmSavingChanges();

      this.isChangingSire = false;
      this.newSireId = "";
    },
  },
  watch: {
    selectedPuppyId: function (newVal: String) {
      if (!newVal || this.dogInfo.puppyId != newVal) {
        //if puppy id cleared, clear all related data
        this.dogInfo.litterId = null;
        this.dogInfo.body = null;
        this.dogInfo.sex = null;
        this.dogInfo.birthdate = null;
        this.dogInfo.puppyId = null;
        this.puppyInfo.dogId = null;
      }
    },
    "dogInfo.registrationType": function (newVal: String, oldVal: String) {
      if (newVal && this.ascaDogsRegistrations.includes(newVal?.trim())) {
        this.dogInfo.ascaRegistered = true;
        this.dogInfo.breed = "AS";

        if (this.isEditMode && newVal?.trim() === "LEP") {
          this.dogInfo.notForBreeding = true;
        }
      } else if (oldVal) {
        //only update if there was previous value selected, otherwise leave values that were persisted
        this.dogInfo.ascaRegistered = false;
        this.dogInfo.breed = null;
      }
      if (newVal && oldVal) {
        this.dogInfo.isRevoked = false;
      }
    },
    dogId: function (newVal: String) {
      this.validationSequence++;
      this.saveSequence++;
      this.editRequestSequence++;
      this.isSaving = false;
      this.isValidating = false;
      this.isGeneratingDogId = false;
      this.isLoadingPuppyInfo = false;
      this.dogInfoBeforeEdit = null;
      this.validatedDogInfo = null;
      this.reloadData();
    },
  },
  beforeDestroy: function () {
    this.validationSequence++;
    this.saveSequence++;
    this.registrationLoadSequence++;
    this.editRequestSequence++;
    this.isLoadingPuppyInfo = false;
  },
});
</script>

<style scoped>
.registration-details {
  position: relative;
}

.notes-text {
  white-space: pre-wrap;
}
</style>
